/**
 * WP-V2-PL — Basen (PLAN-V2 §6 Pool + §10).
 * Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera+WalkController (setupWorld/follow).
 * Zachowuje wejście do MgSwimRace (strój kąpielowy) — bez game-over.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { worldBackButton } from '../../core/worldBackButton.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import { hasSwimOutfit } from './hasSwimOutfit.js';
import {
  INTERACT_BUS_EVENT,
  poolMissionFromInteract,
} from './missionHook.js';
import {
  POOL_TEXTURE_SHAPES,
  POOL_WORLD_H,
  POOL_WORLD_W,
  cabinChangeSwimwear,
  isCabin,
  isPoolBall,
  isRing,
  isSplashPad,
  isTrampoline,
  isUmbrella,
  loungerAction,
  poolColliders,
  poolObjectDefs,
  poolPlacements,
  poolShapeForTexture,
  poolSnapZones,
  poolTextureKeys,
  ringFloatAction,
  showerAction,
  splashPadAction,
  toggleRing,
  trampolineAction,
  type CurtainState,
  type PoolDecorKind,
  type WearState,
} from './objects.js';

const POOL_TEX_SIZE = 160;

/**
 * Upewnij się, że wszystkie tekstury basenu istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie (bez emoji).
 */
export function ensurePoolTextures(scene: Phaser.Scene): void {
  for (const key of poolTextureKeys()) {
    ensurePoolTexture(scene, key);
  }
  // stany (obj_<id>_<state>) — ten sam rysunek bazowy gdy brak wariantu
  for (const def of poolObjectDefs()) {
    ensurePoolTexture(scene, def.texture);
    if (def.states) {
      for (const s of def.states) {
        const stateKey = `obj_${def.id}_${s}`;
        if (!scene.textures.exists(stateKey)) {
          // skopiuj bazową teksturę przez ponowne narysowanie
          const kind =
            poolShapeForTexture(def.texture) ??
            POOL_TEXTURE_SHAPES[def.texture];
          if (kind) {
            const g = scene.make.graphics({ x: 0, y: 0 });
            drawPoolShape(g, kind, POOL_TEX_SIZE, POOL_TEX_SIZE);
            g.generateTexture(stateKey, POOL_TEX_SIZE, POOL_TEX_SIZE);
            g.destroy();
          }
        }
      }
    }
  }
}

export function ensurePoolTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = poolShapeForTexture(textureKey);
  if (!kind) return textureKey;
  const g = scene.make.graphics({ x: 0, y: 0 });
  drawPoolShape(g, kind, POOL_TEX_SIZE, POOL_TEX_SIZE);
  g.generateTexture(textureKey, POOL_TEX_SIZE, POOL_TEX_SIZE);
  g.destroy();
  return textureKey;
}

export function drawPoolShape(
  g: Phaser.GameObjects.Graphics,
  kind: PoolDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'cabin':
      drawCabin(g, cx, cy);
      break;
    case 'lounger':
      drawLounger(g, cx, cy);
      break;
    case 'ball':
      drawBeachBall(g, cx, cy);
      break;
    case 'ring':
      drawRing(g, cx, cy);
      break;
    case 'shower':
      drawShower(g, cx, cy);
      break;
    case 'trampoline':
      drawTrampoline(g, cx, cy);
      break;
    case 'splash':
      drawSplashPad(g, cx, cy);
      break;
    case 'towel':
      drawTowel(g, cx, cy);
      break;
    case 'umbrella':
      drawUmbrella(g, cx, cy);
      break;
  }
}

function drawCabin(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffe4f0, 1);
  g.fillRoundedRect(cx - 48, cy - 52, 96, 100, 10);
  g.lineStyle(4, 0xffffff, 0.95);
  g.strokeRoundedRect(cx - 48, cy - 52, 96, 100, 10);
  // kotara
  g.fillStyle(0xce93d8, 0.95);
  g.fillRoundedRect(cx - 36, cy - 40, 72, 80, 6);
  g.fillStyle(0xe1bee7, 0.7);
  for (let i = 0; i < 4; i++) {
    g.fillRect(cx - 28 + i * 16, cy - 36, 6, 72);
  }
  // daszek
  g.fillStyle(0xf8bbd0, 1);
  g.fillTriangle(cx - 54, cy - 48, cx + 54, cy - 48, cx, cy - 72);
  g.fillStyle(0xffc1e3, 0.4);
  g.fillEllipse(cx, cy + 56, 70, 14);
}

function drawLounger(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffcc80, 1);
  g.fillRoundedRect(cx - 52, cy - 8, 104, 28, 8);
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 48, cy - 44, 40, 40, 8);
  g.fillStyle(0xffe0b2, 0.9);
  g.fillRect(cx - 42, cy - 36, 28, 8);
  g.fillRect(cx - 42, cy - 22, 28, 8);
  // nogi
  g.fillStyle(0xa1887f, 1);
  g.fillRect(cx - 44, cy + 18, 8, 18);
  g.fillRect(cx + 36, cy + 18, 8, 18);
  g.fillStyle(0x90caf9, 0.45);
  g.fillEllipse(cx, cy + 44, 90, 16);
}

function drawBeachBall(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // pastelowe „ćwiartki" piłki plażowej (bez emoji)
  g.fillStyle(0xff8a80, 1);
  g.fillCircle(cx, cy, 48);
  g.fillStyle(0xfff176, 1);
  g.fillTriangle(cx, cy, cx + 48, cy, cx, cy - 48);
  g.fillStyle(0x80d8ff, 1);
  g.fillTriangle(cx, cy, cx, cy - 48, cx - 48, cy);
  g.fillStyle(0xce93d8, 1);
  g.fillTriangle(cx, cy, cx - 48, cy, cx, cy + 48);
  g.fillStyle(0xa5d6a7, 1);
  g.fillTriangle(cx, cy, cx, cy + 48, cx + 48, cy);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx, cy, 12);
  g.lineStyle(3, 0xffffff, 0.95);
  g.strokeCircle(cx, cy, 48);
  g.fillStyle(0x81d4fa, 0.35);
  g.fillEllipse(cx, cy + 52, 60, 12);
}

function drawRing(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // pierścień z przezroczystym środkiem (linia + obrys)
  g.lineStyle(18, 0xff7043, 1);
  g.strokeCircle(cx, cy, 36);
  g.lineStyle(4, 0xffffff, 0.95);
  g.strokeCircle(cx, cy, 46);
  g.strokeCircle(cx, cy, 26);
  g.fillStyle(0xffffff, 1);
  g.fillRect(cx - 8, cy - 50, 16, 14);
  g.fillStyle(0xffcc80, 1);
  g.fillCircle(cx - 30, cy - 10, 7);
  g.fillCircle(cx + 30, cy + 12, 7);
  g.fillStyle(0x81d4fa, 0.3);
  g.fillEllipse(cx, cy + 54, 56, 12);
}

function drawShower(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xb0bec5, 1);
  g.fillRoundedRect(cx - 8, cy - 50, 16, 70, 4);
  g.fillStyle(0x90a4ae, 1);
  g.fillRoundedRect(cx - 28, cy - 58, 56, 16, 6);
  // głowica
  g.fillStyle(0x78909c, 1);
  g.fillEllipse(cx, cy - 42, 50, 18);
  // krople
  g.fillStyle(0x81d4fa, 0.9);
  for (let i = 0; i < 5; i++) {
    g.fillCircle(cx - 16 + i * 8, cy - 20 + (i % 2) * 10, 4);
  }
  g.fillStyle(0x80cbc4, 1);
  g.fillRoundedRect(cx - 22, cy + 20, 44, 12, 4);
  g.fillStyle(0xb2dfdb, 0.5);
  g.fillEllipse(cx, cy + 48, 50, 12);
}

function drawTrampoline(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x5d4037, 1);
  g.fillEllipse(cx, cy + 18, 110, 36);
  g.fillStyle(0x7e57c2, 1);
  g.fillEllipse(cx, cy + 8, 96, 28);
  g.fillStyle(0xb39ddb, 0.9);
  g.fillEllipse(cx, cy + 4, 70, 18);
  g.lineStyle(4, 0xffffff, 0.85);
  g.strokeEllipse(cx, cy + 8, 96, 28);
  // nogi
  g.fillStyle(0x8d6e63, 1);
  g.fillRect(cx - 40, cy + 24, 8, 22);
  g.fillRect(cx + 32, cy + 24, 8, 22);
}

function drawSplashPad(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x4fc3f7, 0.95);
  g.fillEllipse(cx, cy + 8, 110, 50);
  g.fillStyle(0x81d4fa, 0.9);
  g.fillEllipse(cx, cy, 90, 36);
  g.fillStyle(0xffffff, 0.55);
  g.fillEllipse(cx - 16, cy - 6, 28, 12);
  // fontanna centralna
  g.fillStyle(0x29b6f6, 1);
  g.fillCircle(cx, cy - 8, 10);
  g.fillStyle(0xb3e5fc, 0.8);
  g.fillCircle(cx - 14, cy - 28, 6);
  g.fillCircle(cx + 12, cy - 34, 5);
  g.fillCircle(cx, cy - 40, 4);
  g.lineStyle(3, 0xffffff, 0.7);
  g.strokeEllipse(cx, cy + 8, 110, 50);
}

function drawTowel(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xff8a80, 1);
  g.fillRoundedRect(cx - 40, cy - 48, 80, 96, 8);
  g.fillStyle(0xffffff, 0.9);
  for (let i = 0; i < 4; i++) {
    g.fillRect(cx - 34, cy - 36 + i * 20, 68, 8);
  }
  g.fillStyle(0xffcdd2, 1);
  g.fillRoundedRect(cx - 40, cy - 48, 80, 14, 6);
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 40, cy - 48, 80, 96, 8);
}

function drawUmbrella(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // maszt
  g.fillStyle(0xa1887f, 1);
  g.fillRect(cx - 4, cy - 20, 8, 70);
  // daszek
  g.fillStyle(0xff7043, 1);
  g.fillTriangle(cx - 54, cy - 8, cx + 54, cy - 8, cx, cy - 58);
  g.fillStyle(0xfff176, 1);
  g.fillTriangle(cx - 28, cy - 8, cx + 28, cy - 8, cx, cy - 58);
  g.fillStyle(0x4fc3f7, 1);
  g.fillTriangle(cx - 12, cy - 8, cx + 12, cy - 8, cx, cy - 58);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx, cy - 58, 6);
  g.fillStyle(0xffcc80, 0.5);
  g.fillEllipse(cx, cy + 52, 50, 12);
}

/** Ratownik NPC — InteractTarget (fala / serduszka). */
class LifeguardNpc implements InteractTarget {
  x: number;
  y: number;
  isMoving = false;
  readonly body: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private bubble: Phaser.GameObjects.Text | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    const body = scene.add.circle(0, 0, 36, 0xfff3e0).setStrokeStyle(3, 0xe65100);
    const dogKey = scene.textures.exists('dog_maltese_idle')
      ? 'dog_maltese_idle'
      : scene.textures.exists('ph_dog')
        ? 'ph_dog'
        : null;
    const face = dogKey
      ? scene.add.image(0, -2, dogKey).setDisplaySize(56, 56)
      : scene.add.circle(0, -2, 22, 0xffe0b2).setStrokeStyle(2, 0xffffff);
    // kapelusz ratownika (koło) — bez emoji
    const hatOuter = scene.add.circle(0, -36, 16, 0xff7043).setStrokeStyle(2, 0xffffff);
    const hatInner = scene.add.circle(0, -36, 7, 0xffffff);
    const tag = scene.add
      .text(0, 50, 'Ratownik', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5, 0);
    this.body = scene.add.container(x, y, [body, face, hatOuter, hatInner, tag]);
    this.body.setDepth(y + 2);
    this.body.setSize(80, 80);
    this.body.setInteractive(
      new Phaser.Geom.Rectangle(-44, -44, 88, 88),
      Phaser.Geom.Rectangle.Contains,
    );
    this.body.on('pointerdown', () => {
      playFx(this.scene, 'hearts', this.x, this.y - 40);
      void this.playAction('wave');
      this.say('Pływajcie ostrożnie!');
    });
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  moveTo(_x: number, _y: number): void {
    /* statyczny ratownik */
  }

  holdItem(_textureKey: string | null): void {}

  async playAction(a: string): Promise<void> {
    const scale = a === 'happy' || a === 'wave' ? 1.18 : 1.1;
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.body,
        scaleX: scale,
        scaleY: scale,
        duration: 180,
        yoyo: true,
        onComplete: () => resolve(),
      });
    });
  }

  say(msg: string): void {
    this.bubble?.destroy();
    this.bubble = this.scene.add
      .text(this.x, this.y - 80, msg, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#e65100',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(8000);
    this.scene.tweens.add({
      targets: this.bubble,
      y: this.y - 120,
      alpha: 0,
      duration: 1400,
      onComplete: () => {
        this.bubble?.destroy();
        this.bubble = null;
      },
    });
  }

  destroy(): void {
    this.bubble?.destroy();
    this.body.destroy(true);
  }
}

export class PoolScene extends BaseScene {
  static readonly KEY = 'Pool';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private lifeguard: LifeguardNpc | null = null;
  private unsubLayer: (() => void) | null = null;

  private curtain: CurtainState = 'closed';
  private wearingRing: WearState = 'off';
  private hasSuit = false;

  private tipText: Phaser.GameObjects.Text | null = null;
  private tipBubble: Phaser.GameObjects.Rectangle | null = null;
  private suitStatus: Phaser.GameObjects.Text | null = null;
  private curtainVisual: Phaser.GameObjects.Rectangle | null = null;
  private ringHalo: Phaser.GameObjects.Ellipse | null = null;
  private streamNodes: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(PoolScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_pool');
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    void preloadAnchors(this);
  }

  create(): void {
    setupWorld(this, {
      worldW: POOL_WORLD_W,
      worldH: POOL_WORLD_H,
      bg: 0x7ec8e3,
    });
    this.refreshSwimState();
    // PNG z preload; gdy brak — proceduralne kształty (bez emoji-fallbacków)
    try {
      ensurePoolTextures(this);
    } catch {
      /* headless */
    }
    this.drawPoolRoom();
    this.spawnPlayer();
    this.spawnLifeguardAndLayer();
    this.spawnRaceEntry();
    this.spawnBackButton();
    this.spawnSuitStatus();

    this.events.once('shutdown', () => this.teardown());
    this.registry.set('companionFollow', { x: 200, y: 480 });
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
      this.registry.set('companionFollow', { x: pos.x, y: pos.y });
      if (this.ringHalo) {
        this.ringHalo.setPosition(pos.x, pos.y + 18);
        this.ringHalo.setVisible(this.wearingRing === 'on');
      }
    }
    this.layer?.update(dt);
  }

  private refreshSwimState(): void {
    const cache = OutfitStore.load();
    this.hasSuit = hasSwimOutfit(cache.swimSlots);
  }

  private drawPoolRoom(): void {
    if (this.textures.exists('bg_pool')) {
      this.add
        .image(POOL_WORLD_W / 2, POOL_WORLD_H / 2, 'bg_pool')
        .setDisplaySize(POOL_WORLD_W, POOL_WORLD_H)
        .setDepth(-1000);
    }

    // głęboki basen (tafla)
    this.add
      .rectangle(1000, 420, 720, 280, 0x4fa3d1, 0.75)
      .setStrokeStyle(6, 0xffffff)
      .setDepth(2);
    for (let i = 0; i < 5; i++) {
      this.add
        .ellipse(780 + i * 100, 360 + (i % 2) * 18, 90, 28, 0x6ec6e6, 0.4)
        .setStrokeStyle(2, 0xffffff, 0.35)
        .setDepth(3);
    }
    // tory
    for (let i = 0; i < 4; i++) {
      this.add
        .rectangle(1000, 340 + i * 40, 680, 3, 0xffffff, 0.5)
        .setDepth(4);
    }
    // boja
    this.add.circle(1320, 300, 22, 0xff6b6b).setStrokeStyle(3, 0xffffff).setDepth(5);
    this.add.circle(1320, 300, 8, 0xffffff).setDepth(6);

    // brodzik dekor (pod obiektem)
    this.add
      .ellipse(320, 600, 210, 100, 0x90e0ef, 0.55)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(2);

    // kabina tło + kotara
    this.add
      .rectangle(160, 280, 140, 130, 0xffe4f0)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(4);
    this.curtainVisual = this.add
      .rectangle(160, 280, 100, 110, 0xce93d8, 0.85)
      .setStrokeStyle(2, 0x8e24aa)
      .setDepth(5);

    this.add
      .text(POOL_WORLD_W / 2, 36, 'Basen', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
    this.add
      .text(POOL_WORLD_W / 2, 72, 'Przebierz się · pluskaj · skacz z trampoliny!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
  }

  private spawnPlayer(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    this.hasSuit = hasSwimOutfit(cache.swimSlots);
    const startX = 200;
    const startY = 500;
    // strój z OutfitStore zawsze na piesku (nie nagi spawn)
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: startX,
      y: startY,
      breed,
      color,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      mode: this.hasSuit ? 'swim' : 'normal',
      depth: 100,
    });

    this.walk = new WalkController(this, this.avatar, {
      colliders: poolColliders(),
      bounds: {
        minX: 48,
        minY: 140,
        maxX: POOL_WORLD_W - 48,
        maxY: POOL_WORLD_H - 48,
      },
      enableInput: true,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });

    this.ringHalo = this.add
      .ellipse(startX, startY + 18, 70, 28, 0xff7043, 0.35)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(90)
      .setVisible(false);
  }

  private spawnLifeguardAndLayer(): void {
    this.lifeguard = new LifeguardNpc(this, 1380, 480);
    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => {
        self.walk?.moveTo(x, y);
      },
      holdItem: (tex) => {
        self.avatar?.holdItem(tex);
      },
      playAction: (a) => {
        if (!self.avatar) return Promise.resolve();
        if (a === 'sniff') return self.avatar.playAction('happy');
        const known = a as
          | 'sit'
          | 'jump'
          | 'wave'
          | 'eat'
          | 'swim'
          | 'sleep'
          | 'happy';
        return self.avatar.playAction(known);
      },
      getPosition: () =>
        self.walk?.position ?? { x: self.avatar?.x ?? 0, y: self.avatar?.y ?? 0 },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.avatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.avatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: poolObjectDefs(),
      target: playerTarget,
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: POOL_WORLD_W - 48,
        minY: 380,
        maxY: POOL_WORLD_H - 48,
      },
    });
    this.layer.setSnapZones(poolSnapZones());
    this.layer.spawn(poolPlacements());
    // G9 / Toca Boca: zero widocznego tekstu na obiektach — hotspot + lektor, bez podpisów.

    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private dogInRangeOf(evt: InteractEvent): boolean {
    if (
      evt.type === 'intention-done' ||
      evt.type === 'toggle' ||
      evt.type === 'bounce' ||
      evt.type === 'consume'
    ) {
      return true;
    }
    const pos = this.walk?.position;
    if (!pos) return true;
    return Math.hypot(pos.x - evt.x, pos.y - evt.y) <= 110;
  }

  private onInteract(evt: InteractEvent): void {
    this.events.emit('interact', evt);

    // bounce piłki — FX już z silnika
    if (evt.type === 'bounce' && isPoolBall(evt.defId)) {
      playFx(this, 'stars', evt.x, evt.y);
      void this.avatar?.playAction('jump');
      return;
    }

    // kabina: toggle kotary + strój kąpielowy
    if (evt.type === 'toggle' && isCabin(evt.defId)) {
      this.handleCabin();
      return;
    }

    // parasol: toggle otwórz/zamknij (fx + reakcja psa już z silnika)
    if (evt.type === 'toggle' && isUmbrella(evt.defId)) {
      this.handleUmbrella(evt);
      return;
    }

    const isAct = evt.type === 'tap' || evt.type === 'intention-done' || evt.type === 'pickup';
    if (!isAct) return;
    if (evt.type === 'tap' && !this.dogInRangeOf(evt)) return;

    if (isRing(evt.defId)) {
      this.handleRing(evt);
      return;
    }
    if (isSplashPad(evt.defId)) {
      this.handleSplash(evt);
      return;
    }
    if (isTrampoline(evt.defId)) {
      this.handleTrampoline(evt);
      return;
    }
    if (showerAction(evt.defId)) {
      this.handleShower(evt);
      return;
    }
    const lounge = loungerAction(evt.defId);
    if (lounge) {
      void this.lieOnLounger(evt.x, evt.y, lounge);
    }
  }

  /**
   * WAVE7-C: piesek podchodzi i kładzie się NA leżaku (nie ikona obok).
   */
  private async lieOnLounger(
    x: number,
    y: number,
    action: 'sleep',
  ): Promise<void> {
    if (!this.avatar) return;
    this.walk?.moveTo(x, y + 10);
    // poczekaj aż dojdzie (max ~2 s)
    const deadline = Date.now() + 2200;
    while (Date.now() < deadline) {
      const pos = this.walk?.position ?? { x: this.avatar.x, y: this.avatar.y };
      if (Math.hypot(pos.x - x, pos.y - (y + 10)) < 36) break;
      await new Promise<void>((r) => this.time.delayedCall(50, () => r()));
    }
    this.walk?.setStateForTest({
      x,
      y: y + 10,
      tx: x,
      ty: y + 10,
      mode: 'idle',
      vx: 0,
      vy: 0,
    });
    this.avatar.container.setPosition(x, y + 10);
    this.avatar.setVelocity(0, 0);
    playFx(this, 'hearts', x, y);
    await this.avatar.playAction(action);
    const msg = this.add
      .text(x, y - 50, 'Leżę na leżaku… 😴', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6a1b9a',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: msg,
      y: y - 80,
      alpha: 0,
      duration: 900,
      onComplete: () => msg.destroy(),
    });
  }

  private handleUmbrella(evt: InteractEvent): void {
    const open = evt.state !== 'closed';
    const msg = this.add
      .text(evt.x, evt.y - 60, open ? 'Parasol otwarty! ⛱️' : 'Parasol złożony', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6a1b9a',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: msg,
      y: evt.y - 90,
      alpha: 0,
      duration: 700,
      onComplete: () => msg.destroy(),
    });
  }

  private handleCabin(): void {
    const cache = OutfitStore.load();
    const result = cabinChangeSwimwear(this.curtain, cache.swimSlots.suit);
    this.curtain = result.curtain;

    // open → kotara odsunięta w bok
    if (this.curtainVisual) {
      if (this.curtain === 'open') {
        this.curtainVisual.setX(100);
        this.curtainVisual.setAlpha(0.35);
      } else {
        this.curtainVisual.setX(160);
        this.curtainVisual.setAlpha(0.85);
      }
    }

    const swim = { ...cache.swimSlots, suit: result.suitId };
    OutfitStore.saveSwimSlots(swim);
    this.avatar?.setOutfit(cache.slots, swim);
    this.avatar?.setMode(result.mode);
    this.hasSuit = true;
    this.refreshSuitStatus();

    playFx(this, 'sparkle', 160, 240);
    void this.avatar?.playAction('happy');

    const msg = this.add
      .text(160, 180, 'Strój kąpielowy! 👙', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6a1b9a',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: msg,
      y: 140,
      alpha: 0,
      duration: 900,
      onComplete: () => msg.destroy(),
    });
  }

  private handleRing(evt: InteractEvent): void {
    this.wearingRing = toggleRing(this.wearingRing);
    if (this.ringHalo) this.ringHalo.setVisible(this.wearingRing === 'on');
    playFx(this, 'splash', evt.x, evt.y);
    if (this.wearingRing === 'on') {
      void this.avatar?.playAction('happy');
      const float = ringFloatAction(this.wearingRing);
      if (float.action) {
        // lekki podpowiedź
        this.lifeguard?.say('Z kołem pływa się super! 🛟');
      }
    }
  }

  private handleSplash(evt: InteractEvent): void {
    this.refreshSwimState();
    const r = splashPadAction({
      hasSuit: this.hasSuit,
      wearingRing: this.wearingRing === 'on',
    });
    if (!r.ok) {
      this.showNoSuitTip();
      return;
    }
    this.playSplashBurst(evt.x, evt.y);
    if (r.action) void this.avatar?.playAction(r.action);
  }

  private handleTrampoline(evt: InteractEvent): void {
    const t = trampolineAction(this.hasSuit);
    void this.avatar?.playAction(t.action).then(() => {
      if (t.splash) this.playSplashBurst(evt.x + 40, evt.y + 60);
    });
    playFx(this, 'splash', evt.x, evt.y + 40);
    // mały skok wizualny kontenera
    if (this.avatar) {
      const c = this.avatar.container;
      this.tweens.add({
        targets: c,
        y: c.y - 80,
        duration: 280,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }

    // W9-B: krok misji pool_trampoline
    const mission = poolMissionFromInteract(evt);
    if (mission) {
      this.game.events.emit(INTERACT_BUS_EVENT, mission);
      this.events.emit('quest-progress', mission);
    }
  }

  private handleShower(evt: InteractEvent): void {
    // strumień wody
    for (let i = 0; i < 8; i++) {
      const drop = this.add
        .circle(evt.x + (i % 3) * 6 - 6, evt.y - 20, 5, 0x81d4fa, 0.9)
        .setDepth(6000);
      this.streamNodes.push(drop);
      this.tweens.add({
        targets: drop,
        y: evt.y + 50 + i * 3,
        x: evt.x + (Math.random() - 0.5) * 30,
        alpha: 0,
        delay: i * 40,
        duration: 450,
        onComplete: () => {
          drop.destroy();
          this.streamNodes = this.streamNodes.filter((n) => n !== drop);
        },
      });
    }
    playFx(this, 'splash', evt.x, evt.y + 20);
    // otrzepanie
    void this.avatar?.playAction('happy');
    if (this.avatar) {
      this.tweens.add({
        targets: this.avatar.container,
        angle: { from: -8, to: 8 },
        duration: 80,
        yoyo: true,
        repeat: 4,
        onComplete: () => {
          this.avatar?.container.setAngle(0);
        },
      });
    }
  }

  private playSplashBurst(x: number, y: number): void {
    playFx(this, 'splash', x, y);
    for (let i = 0; i < 8; i++) {
      const drop = this.add
        .circle(x, y, 7, 0x81d4fa, 0.95)
        .setStrokeStyle(1, 0xffffff, 0.8)
        .setDepth(6500);
      const ang = (Math.PI * 2 * i) / 8;
      this.tweens.add({
        targets: drop,
        x: x + Math.cos(ang) * 55,
        y: y + Math.sin(ang) * 35 - 40,
        alpha: 0,
        duration: 520,
        onComplete: () => drop.destroy(),
      });
    }
  }

  private showNoSuitTip(): void {
    const msg =
      'Załóż strój kąpielowy w przebieralni! 👙\nAlbo koło ratunkowe 🛟 — potem pluskaj 😊';
    this.tipBubble?.destroy();
    this.tipText?.destroy();

    this.tipBubble = this.add
      .rectangle(300, 420, 380, 100, 0xfff8f0)
      .setStrokeStyle(3, 0xffb6c1)
      .setDepth(50)
      .setScrollFactor(0);
    this.tipText = this.add
      .text(300, 420, msg, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '17px',
        color: '#4a3f55',
        align: 'center',
        wordWrap: { width: 350 },
      })
      .setOrigin(0.5)
      .setDepth(51)
      .setScrollFactor(0);

    this.registry.set('companionFollow', { x: 160, y: 400 });
    this.lifeguard?.say('Do przebieralni! 🚪');

    this.time.delayedCall(4000, () => {
      this.tipBubble?.destroy();
      this.tipText?.destroy();
      this.tipBubble = null;
      this.tipText = null;
      this.registry.set('companionFollow', { x: 200, y: 480 });
    });
  }

  private spawnSuitStatus(): void {
    this.suitStatus = this.add
      .text(this.gameW - 24, 48, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#2d6a4f',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5)
      .setScrollFactor(0)
      .setDepth(20_100)
      .setName('pool-suit-status');
    this.refreshSuitStatus();
  }

  private refreshSuitStatus(): void {
    if (!this.suitStatus) return;
    this.refreshSwimState();
    const status = this.hasSuit
      ? '👙 Strój OK — można do wody!'
      : '👕 Brak stroju kąpielowego';
    this.suitStatus.setText(status);
    this.suitStatus.setColor(this.hasSuit ? '#2d6a4f' : '#9b59b6');
  }

  private spawnRaceEntry(): void {
    const btn = UiKit.button(this, {
      x: this.gameW / 2 + 80,
      y: POOL_WORLD_H - 64,
      label: '🏊 Wyścig pływacki',
      width: 300,
      height: Math.max(80, MIN_TOUCH),
      fill: 0x5dade2,
      textColor: '#ffffff',
      onClick: () => this.tryEnterRace(),
    });
    btn.setScrollFactor(0);
    btn.setDepth(20_100);
    btn.setName('hotspot-MgSwimRace');
    btn.setData('navTarget', 'MgSwimRace');

    this.add
      .text(this.gameW / 2 + 80, POOL_WORLD_H - 18, 'Tory basenowe', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_100);
  }

  private tryEnterRace(): void {
    this.refreshSwimState();
    if (!this.hasSuit) {
      this.showNoSuitTip();
      return;
    }
    getSceneRouter().go('MgSwimRace', { entryPoint: 'world' as const });
  }

  private spawnBackButton(): void {
    worldBackButton(this);
  }

  private teardown(): void {
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.lifeguard?.destroy();
    this.lifeguard = null;
    this.tipBubble?.destroy();
    this.tipText?.destroy();
    this.tipBubble = null;
    this.tipText = null;
    this.suitStatus = null;
    this.curtainVisual = null;
    this.ringHalo?.destroy();
    this.ringHalo = null;
    for (const n of this.streamNodes) n.destroy();
    this.streamNodes = [];
    this.registry.remove('companionFollow');
  }
}

export default PoolScene;
